import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { usePlatform } from '../contexts/PlatformContext';

const isValidPostcode = (p) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test((p || '').trim());

const QuoteLookup = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { policies } = usePlatform();
  const [postcode, setPostcode] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const pc = postcode.trim().toUpperCase();
    const ln = lastName.trim();

    if (!isValidPostcode(pc)) {
      toast({ title: 'Please enter a valid UK postcode', description: 'For example: SW1A 1AA or EC1V 2NX.' });
      return;
    }
    if (!ln || ln.length < 2) {
      toast({ title: 'Please enter your last name', description: 'Use the surname on your welcome letter.' });
      return;
    }

    setBusy(true);
    // Verify postcode via postcodes.io (Royal Mail data source)
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`);
      const j = await res.json();
      if (!res.ok || j.status !== 200) {
        toast({ title: 'Postcode not recognised', description: 'Please double-check and try again.' });
        setBusy(false);
        return;
      }
    } catch {
      toast({ title: 'Postcode lookup unavailable', description: 'Please try again in a moment.' });
      setBusy(false);
      return;
    }

    // Match against any locally-stored policies (agent-created records)
    const normalise = (s) => (s || '').replace(/\s+/g, '').toUpperCase();
    const match = (policies || []).find(
      (p) => normalise(p.postcode) === normalise(pc) && (p.lastName || '').toLowerCase() === ln.toLowerCase()
    );
    setBusy(false);

    if (match) {
      toast({ title: 'Quote found', description: `Opening plan ${match.planNumber} for ${match.firstName} ${match.lastName}.` });
      setTimeout(() => navigate(`/agent/policy/${match.id}`), 400);
    } else {
      toast({
        title: 'No matching quote just yet',
        description: 'We couldn’t find a saved quote for that postcode and surname. Try again or start a new quote.',
      });
    }
  };

  return (
    <section id="quote-lookup" className="py-16 md:py-24 bg-white scroll-mt-24">
      <div className="max-w-6xl mx-auto px-5">
        <div className="relative bg-[#1F0068] text-white rounded-[36px] p-8 md:p-14 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full blob-1 opacity-30" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#E8DDFF] opacity-10" />

          <div className="relative grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                Have a quote from us?
              </h2>
              <p className="mt-3 text-white/80">
                Enter your <b>UK postcode</b> and <b>last name</b> — we’ll bring your quote right back up.
              </p>
            </div>

            <form onSubmit={submit} className="md:col-span-3 grid sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="quote-lookup-form">
              <div className="relative">
                <MapPin className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
                <Input
                  data-testid="quote-lookup-postcode"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  placeholder="Postcode (e.g. EC1V 2NX)"
                  className="h-14 pl-10 rounded-2xl bg-white text-[#1F0068] border-0 font-semibold placeholder:text-[#1F0068]/40"
                />
              </div>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
                <Input
                  data-testid="quote-lookup-lastname"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="h-14 pl-10 rounded-2xl bg-white text-[#1F0068] border-0 font-semibold placeholder:text-[#1F0068]/40"
                />
              </div>
              <Button
                data-testid="quote-lookup-submit"
                type="submit"
                disabled={busy}
                className="squircle h-14 px-7 bg-[#FF5C39] hover:bg-[#e0492b] text-white font-semibold gap-1.5"
              >
                <Search className="w-4 h-4" /> {busy ? 'Searching…' : 'Find my quote'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteLookup;
